import React, { useState, useCallback, useMemo } from "react";

interface TriggerProps {
  id: string;
  "aria-controls": string | undefined;
  "aria-haspopup": "true";
  "aria-expanded": string | undefined;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

interface MenuProps {
  id: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  slotProps: {
    list: {
      "aria-labelledby": string;
    };
  };
}

export interface MenuAnchorReturn {
  open: boolean;
  anchorEl: HTMLElement | null;
  openMenu: (e: React.MouseEvent<HTMLElement>) => void;
  closeMenu: () => void;
  triggerProps: TriggerProps;
  menuProps: MenuProps;
}

export function useMenuAnchor<MenuAnchorReturn>(
  menuId: string,
  triggerId: string = `${menuId}-trigger`,
) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const openMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const triggerProps = useMemo(
    () => ({
      id: triggerId,
      "aria-controls": open ? menuId : undefined,
      "aria-haspopup": "true" as const,
      "aria-expanded": open ? "true" : undefined,
      onClick: openMenu,
    }),
    [triggerId, menuId, open, openMenu],
  );

  const menuProps = useMemo(
    () => ({
      id: menuId,
      anchorEl,
      open,
      onClose: closeMenu,
      slotProps: {
        list: {
          "aria-labelledby": triggerId,
        },
      } as const,
    }),
    [menuId, anchorEl, open, closeMenu, triggerId],
  );

  return { open, anchorEl, openMenu, closeMenu, triggerProps, menuProps };
}
